import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { Space, SpaceParticipant, SpaceMessage, CreateSpaceRequest } from '../types';

// 🎙️ Service Firebase pour la gestion des Spaces
class SpacesService {
  private permissionErrorLogged = false;
  private indexErrorLogged = false;

  /**
   * Vérifier si l'utilisateur est authentifié
   */
  private ensureAuthenticated(): boolean {
    if (!auth.currentUser) {
      console.warn('⚠️ Utilisateur non authentifié pour les Spaces');
      return false;
    }
    return true;
  }
  
  // =====================================
  // 🏗️ GESTION DES SPACES
  // =====================================
  
  /**
   * Créer un nouveau Space
   */
  async createSpace(hostId: string, spaceData: CreateSpaceRequest): Promise<string> {
    try {
      console.log('🎙️ Création Space:', { hostId, spaceData });

      const spaceDoc = {
        title: spaceData.title,
        description: spaceData.description || '',
        postId: spaceData.postId || null,
        createdBy: hostId, // Utilisé par les règles Firestore
        hostId, // Gardé pour compatibilité
        hosts: [hostId], // Liste des hosts
        isLive: false, // Pas encore démarré
        isPublic: spaceData.isPublic,
        maxParticipants: Math.min(spaceData.maxParticipants, 50), // Limite sécurité
        participants: [hostId], // Le host rejoint automatiquement
        speakers: [hostId], // Le host est speaker par défaut
        listeners: [],
        tags: spaceData.tags,
        scheduledFor: spaceData.scheduledFor || null,
        startedAt: null,
        endedAt: null,
        recording: {
          isRecorded: false,
          url: null
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      console.log('📝 Document Space à créer:', spaceDoc);

      const docRef = await addDoc(collection(db, 'spaces'), spaceDoc);
      
      console.log('✅ Space créé avec ID:', docRef.id);
      return docRef.id;
    } catch (error: any) {
      console.error('❌ Erreur création Space:', error);
      
      // Diagnostic spécifique
      if (error?.code === 'permission-denied') {
        console.error('🚨 PERMISSION DENIED lors de la création:');
        console.error('   - Vérifiez que walletAddress est dans le token Firebase');
        console.error('   - Vérifiez les règles Firestore pour la collection spaces');
        console.error('   - Hostid utilisé:', hostId);
      }
      
      throw error;
    }
  }

  /**
   * Démarrer un Space (le rendre live)
   */
  async startSpace(spaceId: string, hostId: string): Promise<void> {
    try {
      const spaceRef = doc(db, 'spaces', spaceId);
      await updateDoc(spaceRef, {
        isLive: true,
        startedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log('🎙️ Space démarré:', spaceId);
    } catch (error) {
      console.error('❌ Erreur démarrage Space:', error);
      throw error;
    }
  }

  /**
   * Arrêter un Space
   */
  async endSpace(spaceId: string, hostId: string): Promise<void> {
    try {
      const spaceRef = doc(db, 'spaces', spaceId);
      await updateDoc(spaceRef, {
        isLive: false,
        endedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log('🛑 Space terminé:', spaceId);
    } catch (error) {
      console.error('❌ Erreur arrêt Space:', error);
      throw error;
    }
  }

  // =====================================
  // 👥 GESTION DES PARTICIPANTS
  // =====================================

  /**
   * Rejoindre un Space
   */
  async joinSpace(spaceId: string, userId: string): Promise<void> {
    try {
      const spaceRef = doc(db, 'spaces', spaceId);
      const spaceSnap = await getDoc(spaceRef);
      
      if (!spaceSnap.exists()) {
        throw new Error('Space non trouvé');
      }
      
      const spaceData = spaceSnap.data() as Space;
      
      // Vérifier si l'utilisateur n'est pas déjà dans le Space
      if (spaceData.participants.includes(userId)) {
        console.log('👤 Utilisateur déjà dans le Space');
        return;
      }
      
      // Vérifier la limite de participants
      if (spaceData.participants.length >= spaceData.maxParticipants) {
        throw new Error('Space complet');
      }
      
      // Ajouter aux participants (en tant que listener par défaut)
      await updateDoc(spaceRef, {
        participants: [...spaceData.participants, userId],
        listeners: [...spaceData.listeners, userId],
        updatedAt: serverTimestamp()
      });
      
      // Ajouter l'entrée participant
      await this.addParticipant(spaceId, userId, 'listener');
      
      console.log('✅ Utilisateur rejoint le Space:', userId);
    } catch (error) {
      console.error('❌ Erreur join Space:', error);
      throw error;
    }
  }

  /**
   * Quitter un Space
   */
  async leaveSpace(spaceId: string, userId: string): Promise<void> {
    try {
      const spaceRef = doc(db, 'spaces', spaceId);
      const spaceSnap = await getDoc(spaceRef);
      
      if (!spaceSnap.exists()) return;
      
      const spaceData = spaceSnap.data() as Space;
      
      // Retirer de toutes les listes
      const updatedParticipants = spaceData.participants.filter(id => id !== userId);
      const updatedSpeakers = spaceData.speakers.filter(id => id !== userId);
      const updatedListeners = spaceData.listeners.filter(id => id !== userId);
      
      await updateDoc(spaceRef, {
        participants: updatedParticipants,
        speakers: updatedSpeakers,
        listeners: updatedListeners,
        updatedAt: serverTimestamp()
      });
      
      // Supprimer l'entrée participant
      await this.removeParticipant(spaceId, userId);
      
      console.log('👋 Utilisateur a quitté le Space:', userId);
    } catch (error) {
      console.error('❌ Erreur leave Space:', error);
      throw error;
    }
  }

  /**
   * Ajouter un participant à la collection space_participants
   */
  private async addParticipant(spaceId: string, userId: string, role: 'host' | 'speaker' | 'listener'): Promise<void> {
    const participantDoc = {
      spaceId,
      userId,
      role,
      isMuted: false,
      isAudioOn: false,
      isHandRaised: false,
      joinedAt: serverTimestamp(),
      lastActiveAt: serverTimestamp(),
      peerId: null
    };

    await addDoc(collection(db, 'space_participants'), participantDoc);
  }

  /**
   * Supprimer un participant de la collection space_participants
   */
  private async removeParticipant(spaceId: string, userId: string): Promise<void> {
    const q = query(
      collection(db, 'space_participants'),
      where('spaceId', '==', spaceId),
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    
    snapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
  }

  // =====================================
  // 📡 ABONNEMENTS TEMPS RÉEL
  // =====================================

  /**
   * S'abonner aux changements d'un Space
   */
  subscribeToSpace(spaceId: string, callback: (space: Space | null) => void): () => void {
    const spaceRef = doc(db, 'spaces', spaceId);
    
    return onSnapshot(spaceRef, (doc) => {
      if (doc.exists()) {
        const space = { id: doc.id, ...doc.data() } as Space;
        callback(space);
      } else {
        callback(null);
      }
    });
  }

  /**
   * S'abonner aux participants d'un Space
   */
  subscribeToSpaceParticipants(spaceId: string, callback: (participants: SpaceParticipant[]) => void): () => void {
    const q = query(
      collection(db, 'space_participants'),
      where('spaceId', '==', spaceId),
      orderBy('joinedAt', 'asc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const participants = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as unknown as SpaceParticipant));
      
      callback(participants);
    });
  }

  /**
   * S'abonner aux Spaces actifs
   */
  subscribeToActiveSpaces(callback: (spaces: Space[]) => void): () => void {
    const q = query(
      collection(db, 'spaces'),
      where('isLive', '==', true),
      orderBy('startedAt', 'desc'),
      limit(20)
    );
    
    return onSnapshot(q, (snapshot) => {
      const spaces = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Space[];
      
      callback(spaces);
    });
  }

  /**
   * Obtenir un Space par ID
   */
  async getSpace(spaceId: string): Promise<Space | null> {
    try {
      const spaceRef = doc(db, 'spaces', spaceId);
      const spaceSnap = await getDoc(spaceRef);
      
      if (spaceSnap.exists()) {
        return { id: spaceSnap.id, ...spaceSnap.data() } as Space;
      }
      
      return null;
    } catch (error) {
      console.error('❌ Erreur récupération Space:', error);
      return null;
    }
  }

  /**
   * Obtenir les Spaces liés à un post
   */
  async getSpacesByPost(postId: string): Promise<Space[]> {
    try {
      // Vérifier l'authentification
      if (!this.ensureAuthenticated()) {
        console.warn('⚠️ Utilisateur non authentifié - retour tableau vide');
        return [];
      }

      console.log('🔍 Recherche Spaces pour post:', postId);

      // Tentative avec gestion d'erreur silencieuse pour les permissions
      try {
        const q = query(
          collection(db, 'spaces'),
          where('postId', '==', postId),
          orderBy('createdAt', 'desc')
        );
        
        const snapshot = await getDocs(q);
        const spaces = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Space[];

        console.log('✅ Spaces trouvés:', spaces.length);
        return spaces;
      } catch (queryError: any) {
        // Si l'erreur est liée aux permissions ou aux index, retourner un tableau vide
        if (queryError?.code === 'permission-denied' || queryError?.code === 'failed-precondition') {
          console.log('⚠️ Index Firebase en construction ou permissions limitées - retour tableau vide');
          return [];
        }
        throw queryError; // Re-lancer les autres erreurs
      }
      
    } catch (error: any) {
      console.error('❌ Erreur récupération Spaces par post:', error);
      
      // Diagnostic spécifique pour les erreurs de permissions
      if (error?.code === 'permission-denied') {
        if (!this.permissionErrorLogged) {
          this.permissionErrorLogged = true;
          console.warn('🚨 PERMISSION DENIED - Causes possibles:');
          console.warn('   1. Indexes Firebase en cours de construction (attendre 5-15 min)');
          console.warn('   2. Règles Firestore non synchronisées');
          console.warn('   3. Token Firebase expiré');
          console.warn('   📱 Vérifiez: https://console.firebase.google.com/project/jupipotes-9a7bd/firestore/indexes');
        }
      } else if (error?.code === 'failed-precondition') {
        if (!this.indexErrorLogged) {
          this.indexErrorLogged = true;
          console.warn('🚨 FAILED PRECONDITION - Index manquant ou en construction');
          console.warn('   Attendez que les indexes soient "Enabled" dans la console Firebase');
        }
      }
      
      return [];
    }
  }

  // =====================================
  // 💬 CHAT DANS LES SPACES
  // =====================================

  /**
   * Envoyer un message dans un Space
   */
  async sendSpaceMessage(spaceId: string, authorId: string, content: string): Promise<string> {
    try {
      const messageDoc = {
        spaceId,
        authorId,
        content: content.trim(),
        type: 'text' as const,
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'space_messages'), messageDoc);
      return docRef.id;
    } catch (error) {
      console.error('❌ Erreur envoi message Space:', error);
      throw error;
    }
  }

  /**
   * S'abonner aux messages d'un Space
   */
  subscribeToSpaceMessages(spaceId: string, callback: (messages: SpaceMessage[]) => void): () => void {
    const q = query(
      collection(db, 'space_messages'),
      where('spaceId', '==', spaceId),
      orderBy('createdAt', 'asc'),
      limit(100)
    );
    
    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SpaceMessage[];
      
      callback(messages);
    });
  }
}

// Instance singleton
export const spacesService = new SpacesService();
export default spacesService;
